import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn, FOCUS_RING } from '@cotera/client/v0/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './dropdown-menu';

/**
 * The pieces two breadcrumb trails share: the chevron between crumbs, and the
 * `…` menu that stands in for the levels which don't fit on the bar.
 *
 * There are two trails because there are two bars — a resource page's own
 * (`ResourcePageShell`) and the artifact panel's (`UnifiedPanelBreadcrumbs`) —
 * and they list different things: folder ancestry in one, a drill-in view stack
 * in the other. What they share is how a path *looks* when it has to fold, which
 * is all that lives here. Each trail keeps its own collapse arithmetic and its
 * own idea of what activating a level does.
 */

/** The chevron between two crumbs. */
export const CrumbSeparator: React.FC<{ className?: string }> = ({
  className,
}) => (
  <ChevronRight
    className={cn('h-3 w-3 shrink-0 text-muted-foreground/40', className)}
    aria-hidden="true"
  />
);

/**
 * Indents a menu row by its depth so the menu reads as a path rather than a
 * flat list. `depth` is 0-based from the top of the listed path.
 */
export const crumbDepthIndent = (depth: number): React.CSSProperties => ({
  paddingLeft: `${0.5 + depth * 0.75}rem`,
});

/**
 * The `…` control standing in for crumbs that don't fit on the bar, and the menu
 * behind it.
 *
 * A literal `…` rather than an icon: at 16px `MoreHorizontal` reads as an
 * overflow *actions* menu, which is a different promise from "there is more path
 * above here".
 *
 * Rows go in as `children` — `DropdownMenuItem`s, indented with
 * {@link crumbDepthIndent}. They navigate from `onSelect` rather than wrapping a
 * link with `asChild`: Radix's roving focus needs a ref on whatever it clones,
 * and the crumb link components don't forward one, so `asChild` focuses `null`.
 */
export const CollapsedCrumbMenu: React.FC<{
  /**
   * The trigger's `aria-label`, and the only thing a screen reader gets — the
   * `…` itself says nothing. Passed in rather than built from a count here: a
   * folder path folds "levels" while a drill-in stack folds "levels above", and
   * the difference is the one thing about this control worth saying precisely.
   */
  label: string;
  /** Caps the menu's width; the two trails size differently. */
  contentClassName?: string;
  children: React.ReactNode;
}> = ({ label, contentClassName, children }) => (
  <DropdownMenu>
    <DropdownMenuTrigger
      className={cn(
        'shrink-0 cursor-pointer rounded px-1 text-sm leading-none text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        FOCUS_RING
      )}
      aria-label={label}
    >
      …
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="start"
      className={cn('max-h-[20rem] overflow-y-auto', contentClassName)}
    >
      {children}
    </DropdownMenuContent>
  </DropdownMenu>
);
